import React from 'react';
import { styled } from '@compiled/react';

import { Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Countdown } from './countdown';

type Props = {
	src: string;
	type: 'video/mp4';
};

export const LoomVideoPlayer = ({ src, type }: Props) => (
	<Flex
		direction="column"
		alignItems="center"
		gap="space.200"
		justifyContent="center"
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		xcss={xcss({
			position: 'relative',
		})}
	>
		<Video loop muted playsInline autoPlay>
			<source type={type} src={src} />
		</Video>
		<VideoControls>
			<VideoPlayerButtonContainer>
				<svg width="25" height="26" viewBox="0 0 25 26" fill="none">
					<rect
						x="0.197998"
						y="0.97168"
						width="24.7986"
						height="24.7986"
						rx="3.09982"
						fill="#FF613D"
					/>
				</svg>
			</VideoPlayerButtonContainer>
			<Countdown />
			<VideoPlayerButtonContainer>
				<button aria-label="Pause recording in animation" type="button">
					<svg
						width="20"
						height="23"
						viewBox="0 0 20 23"
						fill="none"
						color={token('color.icon', '#44546F')}
					>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M15.9654 0.521484C14.0929 0.521484 12.5749 2.03943 12.5749 3.91192V18.8298C12.5749 20.7023 14.0929 22.2203 15.9654 22.2203C17.8378 22.2203 19.3558 20.7023 19.3558 18.8298V3.91192C19.3558 2.03943 17.8378 0.521484 15.9654 0.521484ZM3.75982 0.521491C1.88733 0.521491 0.369385 2.03944 0.369385 3.91192V18.8298C0.369385 20.7023 1.88733 22.2203 3.75982 22.2203C5.6323 22.2203 7.15025 20.7023 7.15025 18.8298V3.91192C7.15025 2.03944 5.6323 0.521491 3.75982 0.521491Z"
							fill="currentColor"
						/>
					</svg>
				</button>
			</VideoPlayerButtonContainer>
			<VideoPlayerButtonContainer>
				<button aria-label="Cancel recording in animation" type="button">
					<svg
						width="26"
						height="29"
						viewBox="0 0 26 29"
						fill="none"
						color={token('color.icon', '#44546F')}
					>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M0.266113 6.93172C0.266113 6.07573 0.960033 5.38181 1.81603 5.38181H24.1348C24.9908 5.38181 25.6847 6.07573 25.6847 6.93172C25.6847 7.78771 24.9908 8.48163 24.1348 8.48163H1.81603C0.960033 8.48163 0.266113 7.78771 0.266113 6.93172Z"
							fill="currentColor"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M10.4951 3.5217C10.2485 3.5217 10.0119 3.61968 9.83754 3.79408C9.66314 3.96847 9.56516 4.20501 9.56516 4.45165V5.3816H16.3848V4.45165C16.3848 4.20501 16.2868 3.96847 16.1124 3.79408C15.938 3.61968 15.7015 3.5217 15.4548 3.5217H10.4951ZM19.4846 5.3816V4.45165C19.4846 3.38288 19.06 2.3579 18.3043 1.60217C17.5486 0.846439 16.5236 0.421875 15.4548 0.421875H10.4951C9.42635 0.421875 8.40136 0.846439 7.64563 1.60217C6.8899 2.3579 6.46534 3.38288 6.46534 4.45165V5.3816H4.29546C3.43947 5.3816 2.74555 6.07551 2.74555 6.93151V24.2905C2.74555 25.3593 3.17011 26.3843 3.92584 27.14C4.68157 27.8957 5.70656 28.3203 6.77532 28.3203H19.1746C20.2434 28.3203 21.2684 27.8957 22.0241 27.14C22.7798 26.3843 23.2044 25.3593 23.2044 24.2905V6.93151C23.2044 6.07551 22.5105 5.3816 21.6545 5.3816H19.4846ZM5.84537 8.48142V24.2905C5.84537 24.5372 5.94335 24.7737 6.11775 24.9481C6.29215 25.1225 6.52868 25.2205 6.77532 25.2205H19.1746C19.4213 25.2205 19.6578 25.1225 19.8322 24.9481C20.0066 24.7737 20.1046 24.5372 20.1046 24.2905V8.48142H5.84537Z"
							fill="currentColor"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M10.4951 11.5815C11.3511 11.5815 12.045 12.2754 12.045 13.1314V20.5709C12.045 21.4269 11.3511 22.1209 10.4951 22.1209C9.63912 22.1209 8.9452 21.4269 8.9452 20.5709V13.1314C8.9452 12.2754 9.63912 11.5815 10.4951 11.5815Z"
							fill="currentColor"
						/>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M15.455 11.5815C16.311 11.5815 17.005 12.2754 17.005 13.1314V20.5709C17.005 21.4269 16.311 22.1209 15.455 22.1209C14.599 22.1209 13.9051 21.4269 13.9051 20.5709V13.1314C13.9051 12.2754 14.599 11.5815 15.455 11.5815Z"
							fill="currentColor"
						/>
					</svg>
				</button>
			</VideoPlayerButtonContainer>
		</VideoControls>
	</Flex>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Video = styled.video({
	width: '296px',
	borderRadius: '50%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const VideoControls = styled.div({
	gap: token('space.100', '8px'),
	position: 'absolute',
	width: '320px',
	height: '74px',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	padding: `${token('space.050', '4px')} ${token('space.250', '20px')}`,
	borderRadius: token('space.600', '3.125em'),
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	boxShadow: '0 0 0 0.3125rem hsla(0, 0%, 100%, 0.4)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-around',
	bottom: '-44px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const VideoPlayerButtonContainer = styled.div({
	height: token('space.400', '30px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'button, svg': {
		height: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		margin: 0,
		padding: 0,
		background: 'none',
		border: 'none',
	},
});
